import type { EvaEndpoint } from '@springtree/eva-sdk-core-service';
import type {
  ListOrganizationUnitSummaries,
  ListOrganizationUnitSummariesResponse,
} from '@springtree/eva-services-core';
import type { UseQueryOptions } from '@tanstack/react-query';
import { SvcListOrganizationUnitSummaries } from '@springtree/eva-services-core';
import { useQuery } from '@tanstack/react-query';
import { filterOrganizationsForExpansionMarkets } from '@/core/constants/expansion-markets';
import callEvaService from '@/core/services/eva/call-eva-service';
import { useEvaContext } from '@/ui/context/eva';

export interface OrganizationUnit {
  organizationId: string;
  countryId: string;
  languageId: string;
  name: string;
  currency: string;
}

export const ORGANIZATION_UNIT_SET_ID = 'MINDOASIS_B2C_ALL';

export const queryKey = () => ['eva-organizations'];

export const filterSupportedOrganizations = (
  organizations: ListOrganizationUnitSummariesResponse['Result']['Page'],
) =>
  organizations
    .map(org => {
      const countryCode = org.CountryID;
      const languageCode = org.LanguageID;
      const currencyCode = org.CurrencyID;

      // We need to filter out OUs that are not webshop types, for now we do
      // that by requiring a country, language and currency.
      // TODO: Can this be improved?
      if (countryCode && languageCode && currencyCode) {
        return {
          countryId: countryCode,
          languageId: languageCode,
          currency: currencyCode,
          name: org.Name,
          organizationId: org.ID.toString(),
        };
      }

      return null;
    })
    .filter(Boolean) as OrganizationUnit[];

export const queryFn = async (
  endpoint: EvaEndpoint,
): Promise<OrganizationUnit[]> => {
  const request: ListOrganizationUnitSummaries = {
    PageConfig: {
      Limit: 2000,
      Filter: {
        OrganizationUnitSetID: ORGANIZATION_UNIT_SET_ID,
      },
    },
  };

  const response = await callEvaService(
    endpoint,
    SvcListOrganizationUnitSummaries,
    request,
    {
      cache: 'public',
      revalidate: 3600, // 1 hour
      backendIdMode: 'ExternalIDs', // only backend IDs are supported for this endpoint
      // Use a hardcoded valid org ID (NL) when listing all orgs
      // This is needed when called from non-EVA countries
      requestedOrganizationUnitID: '1',
    },
  );

  if (!response || response.Error) {
    throw new Error(
      response?.Error?.Message || 'Failed to fetch organizations',
    );
  }

  return filterOrganizationsForExpansionMarkets(
    filterSupportedOrganizations(response.Result.Page),
  );
};

const useOrganizationsQuery = (
  options?: Omit<
    UseQueryOptions<Awaited<ReturnType<typeof queryFn>>>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { endpoint } = useEvaContext();

  return useQuery({
    ...options,
    queryKey: queryKey(),
    queryFn: () => queryFn(endpoint),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
};

export default useOrganizationsQuery;
